import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime, parseServerDate } from "src/utils/dateTime";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { IconFile2 } from "@ui/icons/IconFile2";
import { IconFolder } from "@ui/icons/IconFolder";
import Input from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Typo } from "@ui/text/Typo";
import { ArrowDown, ArrowLeft, ArrowUp, ChevronRight, ChevronsUpDown, RefreshCw, Upload } from "lucide-react";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { GD } from "src/GD";
import { notify } from "src/tools/notify";
import {
	DectaFileActions,
	getDectaFilePath,
	isPreviewable,
	loadDectaFileBlob,
	openDectaFilePopup,
	uploadDectaFile,
} from "src/utils/dectaFile";

interface IDectaEntry {
	name: string;
	type: "directory" | "file";
	size: number;
	date: string;
	path: string;
}

type TBrowserFilter = {
	name: string;
	value: string;
};

type TCachedDirectory = {
	files: IDectaEntry[];
	timestamp: number;
};

type TSortField = "name" | "date" | "size";
type TSortDirection = "asc" | "desc";
type TSortPreference = {
	field: TSortField;
	direction: TSortDirection;
};

const CACHE_TTL_MS = 30_000;
const MAX_CACHED_DIRECTORIES = 50;
const directoryCache = new Map<string, TCachedDirectory>();
const ALL_TYPES_FILTER: TBrowserFilter = { get name() { return msg("allTypes"); }, value: "all" };
const FOLDERS_FILTER: TBrowserFilter = { get name() { return msg("folders"); }, value: "directory" };
const DEFAULT_SORT: TSortPreference = { field: "date", direction: "desc" };
const SORT_STORAGE_KEY_PREFIX = "decta-browser.sort.user.";
let lastVisitedPath = "/";

const getSortStorageKey = (userId: number) => `${SORT_STORAGE_KEY_PREFIX}${userId}`;

const loadSortPreference = (userId?: number): TSortPreference => {
	if (!userId) return DEFAULT_SORT;

	try {
		const stored = JSON.parse(localStorage.getItem(getSortStorageKey(userId)) ?? "null") as Partial<TSortPreference> | null;
		const validField = stored?.field === "name" || stored?.field === "date" || stored?.field === "size";
		const validDirection = stored?.direction === "asc" || stored?.direction === "desc";
		return validField && validDirection ? { field: stored.field!, direction: stored.direction! } : DEFAULT_SORT;
	} catch {
		return DEFAULT_SORT;
	}
};

const saveSortPreference = (userId: number | undefined, preference: TSortPreference) => {
	if (!userId) return;

	try {
		localStorage.setItem(getSortStorageKey(userId), JSON.stringify(preference));
	} catch {
		// Sorting still works for the current visit if persistent storage is unavailable.
	}
};

const pruneDirectoryCache = () => {
	const now = Date.now();
	for (const [path, cached] of directoryCache) {
		if (now - cached.timestamp >= CACHE_TTL_MS) directoryCache.delete(path);
	}

	while (directoryCache.size >= MAX_CACHED_DIRECTORIES) {
		const oldestPath = directoryCache.keys().next().value as string | undefined;
		if (!oldestPath) break;
		directoryCache.delete(oldestPath);
	}
};

const normalizePath = (path?: string | null) => {
	if (!path) return "/";
	let normalized = path.trim().replaceAll("\\", "/").replace(/\/{2,}/g, "/");
	if (!normalized.startsWith("/")) normalized = "/" + normalized;
	if (normalized.length > 1) normalized = normalized.replace(/\/+$/, "");
	return normalized || "/";
};

const getParentPath = (path: string) => {
	const parts = normalizePath(path).split("/").filter(Boolean);
	parts.pop();
	return parts.length ? "/" + parts.join("/") : "/";
};

const getEntryPath = (entry: IDectaEntry) => normalizePath(getDectaFilePath(entry.path, entry.name));

const isOutFileSubdirectory = (path: string) => {
	const segments = normalizePath(path).split("/").filter(Boolean);
	return segments.length > 1 && segments[0].toLowerCase() === "out_file";
};

const getFileExtension = (fileName: string) => {
	const lower = fileName.toLowerCase();
	if (lower.endsWith(".txt-simple")) return "txt-simple";
	return lower.includes(".") ? lower.split(".").pop() ?? "" : "";
};

const formatBytes = (value: number) => {
	if (!Number.isFinite(value) || value < 0) return "—";
	if (value >= 1024 * 1024 * 1024) return msg("templateGb_4cc3a76a", { v0: (value / (1024 * 1024 * 1024)).toFixed(2) });
	if (value >= 1024 * 1024) return msg("templateMb_016aac3b", { v0: (value / (1024 * 1024)).toFixed(2) });
	if (value >= 1024) return msg("templateKb_f1dd622b", { v0: (value / 1024).toFixed(2) });
	return `${value} B`;
};

const formatDate = (value: string) => {
	return formatDisplayDateTime(value, { separator: " - " });
};

const navigationButtonSx = {
	width: "38px",
	height: "38px",
	padding: "0px",
	borderRadius: "8px",
	flexShrink: 0,
};

const browserGridColumns = "minmax(280px, 1fr) 190px 100px 36px 24px";

export const DectaBrowser = () => {
	const localeRevision = useLocale();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);
	const [files, setFiles] = useState<IDectaEntry[]>([]);
	const [error, setError] = useState<IError>();
	const [busy, setBusy] = useState(false);
	const [currentPath, setCurrentPath] = useState(() => normalizePath(lastVisitedPath));
	const [backStack, setBackStack] = useState<string[]>([]);
	const [reloadKey, setReloadKey] = useState(0);
	const [search, setSearch] = useState("");
	const [typeFilter, setTypeFilter] = useState<TBrowserFilter>(ALL_TYPES_FILTER);
	const [sortPreference, setSortPreference] = useState<TSortPreference>(() => loadSortPreference(user?.id));
	const [uploading, setUploading] = useState(false);
	const requestIdRef = useRef(0);
	const uploadInputRef = useRef<HTMLInputElement>(null);
	const { field: sortField, direction: sortDirection } = sortPreference;

	useEffect(() => {
		setSortPreference(loadSortPreference(user?.id));
	}, [user?.id]);

	useEffect(() => {
		lastVisitedPath = currentPath;
		const requestId = ++requestIdRef.current;
		pruneDirectoryCache();
		const cached = directoryCache.get(currentPath);
		if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
			setFiles(cached.files);
			setError(undefined);
			setBusy(false);
			return;
		}

		if (cached) directoryCache.delete(currentPath);
		setFiles([]);
		setError(undefined);
		setBusy(true);

		const loadDirectory = async () => {
			try {
				const response = await GD.REQ_API.request({
					method: "processing/statementlist",
					data: { path: currentPath === "/" ? undefined : currentPath },
				});

				if (requestId !== requestIdRef.current) return;
				if (response?.error) {
					setError(response.error);
					return;
				}

				if (!Array.isArray(response?.data)) {
					setError({ code: 0, message: msg("theDirectoryReturnedAnUnexpectedResponse") });
					return;
				}

				const loadedFiles = response.data as IDectaEntry[];
				pruneDirectoryCache();
				directoryCache.set(currentPath, { files: loadedFiles, timestamp: Date.now() });
				setFiles(loadedFiles);
				setError(undefined);
			} catch (loadError) {
				if (requestId !== requestIdRef.current) return;
				setError({ code: 0, message: loadError instanceof Error ? loadError.message : msg("directoryCouldNotBeLoaded") });
			} finally {
				if (requestId === requestIdRef.current) setBusy(false);
			}
		};

		loadDirectory();
	}, [currentPath, reloadKey]);

	const typeOptions = useMemo<TBrowserFilter[]>(() => {
		const extensions = Array.from(
			new Set(files.filter(file => file.type === "file").map(file => getFileExtension(file.name) || "other")),
		).sort((a, b) => a.localeCompare(b));
		return [ALL_TYPES_FILTER, FOLDERS_FILTER, ...extensions.map(extension => ({ name: extension === "other" ? msg("otherFiles") : extension.toUpperCase(), value: extension }))];
	}, [localeRevision, files]);

	const visibleFiles = useMemo(() => {
		const normalizedSearch = search.trim().toLowerCase();
		return files
			.filter(entry => {
				if (normalizedSearch && !entry.name.toLowerCase().includes(normalizedSearch)) return false;
				if (typeFilter.value === "all") return true;
				if (typeFilter.value === "directory") return entry.type === "directory";
				return entry.type === "file" && (getFileExtension(entry.name) || "other") === typeFilter.value;
			})
			.sort((a, b) => {
				if (a.type !== b.type) return a.type === "directory" ? -1 : 1;
				if (a.type === "directory") {
					const comparison = a.name.localeCompare(b.name);
					return sortField === "name" && sortDirection === "desc" ? -comparison : comparison;
				}

				let comparison = 0;
				if (sortField === "name") comparison = a.name.localeCompare(b.name);
				if (sortField === "size") comparison = (Number(a.size) || 0) - (Number(b.size) || 0);
				if (sortField === "date") {
					const aTime = (parseServerDate(a.date)?.getTime() ?? NaN);
					const bTime = (parseServerDate(b.date)?.getTime() ?? NaN);
					comparison = (Number.isNaN(aTime) ? 0 : aTime) - (Number.isNaN(bTime) ? 0 : bTime);
				}

				return comparison * (sortDirection === "asc" ? 1 : -1) || a.name.localeCompare(b.name);
			});
	}, [files, search, sortDirection, sortField, typeFilter]);

	const foldersCount = files.filter(file => file.type === "directory").length;
	const filesCount = files.length - foldersCount;
	const hasActiveFilters = Boolean(search.trim()) || typeFilter.value !== "all";
	const canUpload = isOutFileSubdirectory(currentPath);

	const resetFilters = () => {
		setSearch("");
		setTypeFilter(ALL_TYPES_FILTER);
	};

	const navigate = (path: string, remember = true) => {
		const normalizedPath = normalizePath(path);
		if (normalizedPath === currentPath) return;
		if (remember) setBackStack(stack => [...stack, currentPath]);
		setCurrentPath(normalizedPath);
		resetFilters();
	};

	const goBack = () => {
		const previousPath = backStack.at(-1);
		if (!previousPath) return;
		setBackStack(stack => stack.slice(0, -1));
		setCurrentPath(previousPath);
		resetFilters();
	};

	const refresh = () => {
		directoryCache.delete(currentPath);
		setReloadKey(value => value + 1);
	};

	const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.currentTarget.files?.[0];
		event.currentTarget.value = "";
		if (!file || !canUpload || uploading) return;

		const uploadPath = currentPath;
		setUploading(true);
		try {
			await uploadDectaFile(uploadPath, file);
			directoryCache.delete(uploadPath);
			setReloadKey(value => value + 1);
			notify.success(msg("templateUploaded_b6be62f5", { v0: file.name }));
		} catch (uploadError) {
			notify.error(uploadError instanceof Error ? uploadError.message : msg("fileCouldNotBeUploaded"));
		} finally {
			setUploading(false);
		}
	};

	const changeSort = (field: TSortField) => {
		setSortPreference(current => {
			const next: TSortPreference =
				field === current.field
					? { field, direction: current.direction === "asc" ? "desc" : "asc" }
					: { field, direction: field === "name" ? "asc" : "desc" };
			saveSortPreference(user?.id, next);
			return next;
		});
	};

	const openEntry = (entry: IDectaEntry) => {
		const entryPath = getEntryPath(entry);
		if (entry.type === "directory") {
			navigate(entryPath);
			return;
		}

		openDectaFilePopup({
			name: entry.name,
			path: entryPath,
			previewable: isPreviewable(entry.name),
			load: () => loadDectaFileBlob(entryPath),
		});
	};

	return (
		<VBox gap="12px" sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
			<VBox
				gap="10px"
				sx={{
					padding: "12px",
					backgroundColor: "#fff",
					border: "1px solid rgba(0,0,0,.08)",
					borderRadius: "10px",
					boxShadow: "0 2px 8px rgba(24, 39, 75, .04)",
				}}>
				<HBox gap="8px" sx={{ alignItems: "center" }}>
					<Box title={msg("back")}>
						<Button variant="secondarySmall" icon={<ArrowLeft size={17} />} disabled={!backStack.length} onClick={goBack} sx={navigationButtonSx} />
					</Box>
					<Box title={msg("up")}>
						<Button
							variant="secondarySmall"
							icon={<ArrowUp size={17} />}
							disabled={currentPath === "/"}
							onClick={() => navigate(getParentPath(currentPath))}
							sx={navigationButtonSx}
						/>
					</Box>
					<PathClip path={currentPath} onChange={path => navigate(path)} />
					{canUpload && (
						<>
							<input ref={uploadInputRef} type="file" hidden onChange={handleUpload} />
							<Button
								variant="primarySmall"
								icon={<Upload size={16} />}
								title={uploading ? msg("uploading") : msg("uploadFile")}
								disabled={busy || uploading}
								onClick={() => uploadInputRef.current?.click()}
								sx={{ height: "38px", flexShrink: 0 }}
							/>
						</>
					)}
					<Box title={msg("refresh")}>
						<Button variant="secondarySmall" icon={<RefreshCw size={17} />} disabled={busy || uploading} onClick={refresh} sx={navigationButtonSx} />
					</Box>
				</HBox>

				<HBox gap="10px" sx={{ alignItems: "center", flexWrap: "wrap" }}>
					<Input
						type="search"
						size="small"
						name="search"
						data={{ search }}
						onChange={setSearch}
						placeholder={msg("searchFilesAndFolders")}
						sx={{ flex: "1 1 300px", minWidth: "220px" }}
						sxInput={{ "&::placeholder": { fontStyle: "normal" } }}
					/>
					<Select<TBrowserFilter>
						size="small"
						value={typeFilter}
						values={typeOptions}
						onChange={value => setTypeFilter(typeOptions.find(option => option.value === String(value)) ?? ALL_TYPES_FILTER)}
						sx={{ minWidth: "155px" }}
					/>
					<Box
						sx={{
							padding: "7px 11px",
							borderRadius: "16px",
							backgroundColor: "#f2f4f7",
							whiteSpace: "nowrap",
							fontSize: "12px",
							color: "#667085",
						}}>
						{msg("folderCount", { count: foldersCount, formatted: foldersCount })} · {msg("fileCount", { count: filesCount, formatted: filesCount })}
					</Box>
				</HBox>
			</VBox>

			<ProgressBar busy={busy || uploading} />

			{error && !busy ? (
				<BrowserState icon={<IconFolder size={42} />} title={msg("directoryCouldNotBeLoaded")} description={error.message || msg("unknownError")}>
					<Button variant="secondarySmall" title={msg("retry")} onClick={refresh} />
				</BrowserState>
			) : busy && !files.length ? (
				<BrowserState icon={<RefreshCw size={36} />} title={msg("loadingDirectory")} />
			) : !files.length ? (
				<BrowserState icon={<IconFolder size={42} />} title={msg("thisFolderIsEmpty")} description={msg("thereAreNoFilesOrFoldersHere")} />
			) : !visibleFiles.length ? (
				<BrowserState icon={<IconFile2 size={42} />} title={msg("noMatchingFiles")} description={msg("tryChangingTheSearchOrFileTypeFilter")}>
					{hasActiveFilters && <Button variant="secondarySmall" title={msg("resetFilters")} onClick={resetFilters} />}
				</BrowserState>
			) : (
				<BrowserList entries={visibleFiles} onOpen={openEntry} sortField={sortField} sortDirection={sortDirection} onSort={changeSort} />
			)}
		</VBox>
	);
};

const BrowserList = ({
	entries,
	onOpen,
	sortField,
	sortDirection,
	onSort,
}: {
	entries: IDectaEntry[];
	onOpen: (entry: IDectaEntry) => void;
	sortField: TSortField;
	sortDirection: TSortDirection;
	onSort: (field: TSortField) => void;
}) => { useLocale(); return ((
	<VBox
		sx={{
			minHeight: 0,
			flex: "1 1 auto",
			overflow: "hidden",
			border: "1px solid #e4e7ec",
			borderRadius: "10px",
			backgroundColor: "#fff",
			boxShadow: "0 2px 8px rgba(24, 39, 75, .04)",
		}}>
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: browserGridColumns,
				gap: "12px",
				padding: "9px 14px",
				backgroundColor: "#f8fafc",
				borderBottom: "1px solid #e4e7ec",
				color: "#667085",
				fontSize: "11px",
				fontWeight: 600,
				textTransform: "uppercase",
				letterSpacing: ".04em",
				position: "sticky",
				top: 0,
				zIndex: 2,
				"@media (max-width: 768px)": { gridTemplateColumns: "minmax(0, 1fr) 36px 24px" },
			}}>
			<SortHeader label={msg("name")} field="name" activeField={sortField} direction={sortDirection} onSort={onSort} />
			<SortHeader label={msg("modified")} field="date" activeField={sortField} direction={sortDirection} onSort={onSort} sx={{ "@media (max-width: 768px)": { display: "none" } }} />
			<SortHeader label={msg("size")} field="size" activeField={sortField} direction={sortDirection} onSort={onSort} align="right" sx={{ "@media (max-width: 768px)": { display: "none" } }} />
			<Box />
			<Box />
		</Box>
		<Box sx={{ minHeight: 0, flex: 1, overflow: "auto" }}>
			{entries.map(entry => {
				const extension = entry.type === "file" ? getFileExtension(entry.name) : "";
				return (
					<Box
						key={`${entry.type}:${entry.path}:${entry.name}`}
						onClick={() => onOpen(entry)}
						sx={{
							display: "grid",
							gridTemplateColumns: browserGridColumns,
							gap: "12px",
							alignItems: "center",
							padding: "10px 14px",
							borderBottom: "1px solid #eef0f3",
							cursor: "pointer",
							transition: "background-color .14s",
							"&:last-child": { borderBottom: "none" },
							"&:hover": { backgroundColor: "#f8fafc" },
							"@media (max-width: 768px)": { gridTemplateColumns: "minmax(0, 1fr) 36px 24px" },
						}}>
						<HBox gap="10px" sx={{ alignItems: "center", minWidth: 0 }}>
							<Box
								sx={{
									width: "34px",
									height: "34px",
									borderRadius: "8px",
									backgroundColor: entry.type === "directory" ? "#fff4df" : "#f2f4f7",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flexShrink: 0,
								}}>
								{entry.type === "directory" ? <IconFolder size={22} /> : <IconFile2 size={21} />}
							</Box>
							<Typo variant="medium" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
								{entry.name}
							</Typo>
							{extension && (
								<Box sx={{ padding: "2px 7px", borderRadius: "10px", backgroundColor: "#f2f4f7", color: "#667085", fontSize: "10px", fontWeight: 600, flexShrink: 0 }}>
									{extension.toUpperCase()}
								</Box>
							)}
						</HBox>
						<Typo variant="medium" color="textSecondary" sx={{ whiteSpace: "nowrap", "@media (max-width: 768px)": { display: "none" } }}>
							{entry.type === "file" ? <DateTimeTooltip value={entry.date}>{formatDate(entry.date)}</DateTimeTooltip> : ""}
						</Typo>
						<Typo variant="medium" color="textSecondary" sx={{ textAlign: "right", whiteSpace: "nowrap", "@media (max-width: 768px)": { display: "none" } }}>
							{entry.type === "file" ? formatBytes(entry.size) : ""}
						</Typo>
						<Box sx={{ display: "flex", justifyContent: "center" }}>
							{entry.type === "file" && <DectaFileActions filePath={getEntryPath(entry)} fileName={entry.name} />}
						</Box>
						<ChevronRight size={16} opacity={0.45} />
					</Box>
				);
			})}
		</Box>
	</VBox>
)); };

const SortHeader = ({
	label,
	field,
	activeField,
	direction,
	onSort,
	align,
	sx,
}: {
	label: string;
	field: TSortField;
	activeField: TSortField;
	direction: TSortDirection;
	onSort: (field: TSortField) => void;
	align?: "right";
	sx?: Record<string, unknown>;
}) => {
	useLocale();
	const active = field === activeField;
	return (
		<HBox
			onClick={() => onSort(field)}
			sx={{
				alignItems: "center",
				justifyContent: align === "right" ? "flex-end" : "flex-start",
				gap: "5px",
				cursor: "pointer",
				userSelect: "none",
				color: active ? "#344054" : "#667085",
				"&:hover": { color: "#344054" },
				...sx,
			}}>
			{label}
			{active ? direction === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} /> : <ChevronsUpDown size={13} opacity={0.45} />}
		</HBox>
	);
};

const PathClip = ({ path, onChange }: { path: string; onChange: (path: string) => void }) => {
	useLocale();
	const actualSegments = normalizePath(path).split("/").filter(Boolean);
	const breadcrumbs = [
		{ label: msg("root"), path: "/" },
		...actualSegments.map((segment, index) => {
			return { label: segment, path: "/" + actualSegments.slice(0, index + 1).join("/") };
		}),
	];

	return (
		<HBox
			sx={{
				alignItems: "center",
				flex: "1 1 320px",
				minWidth: 0,
				overflowX: "auto",
				height: "38px",
				padding: "0 10px",
				backgroundColor: "#f8fafc",
				border: "1px solid #e4e7ec",
				borderRadius: "8px",
				scrollbarWidth: "thin",
			}}
			gap="2px">
			<IconFolder size={18} />
			{breadcrumbs.map((breadcrumb, index) => {
				const isCurrent = index === breadcrumbs.length - 1;
				return (
					<HBox key={breadcrumb.path} gap="2px" sx={{ alignItems: "center", flexShrink: 0 }}>
						{index > 0 && <Typo color="textSecondary">/</Typo>}
						<Typo
							onClick={() => !isCurrent && onChange(breadcrumb.path)}
							sx={{
								padding: "3px 5px",
								borderRadius: "4px",
								fontWeight: isCurrent ? 600 : 400,
								cursor: isCurrent ? "default" : "pointer",
								"&:hover": { backgroundColor: isCurrent ? undefined : "rgba(0,0,0,.07)" },
							}}>
							{breadcrumb.label}
						</Typo>
					</HBox>
				);
			})}
		</HBox>
	);
};

const BrowserState = ({
	icon,
	title,
	description,
	children,
}: {
	icon: React.ReactNode;
	title: string;
	description?: string;
	children?: React.ReactNode;
}) => { useLocale(); return ((
	<VBox
		center
		gap="10px"
		sx={{ minHeight: "280px", padding: "24px", border: "1px dashed rgba(0,0,0,.15)", borderRadius: "6px", textAlign: "center", color: "textSecondary" }}>
		{icon}
		<Typo variant="headMedium">{title}</Typo>
		{description && <Typo variant="medium">{description}</Typo>}
		{children && <><Spacer size="4px" />{children}</>}
	</VBox>
)); };
